import React from "react";
import { cx } from "../../../style";
import "./Table.css";

/* --- Примитивы (для кастомной вёрстки, когда data-driven не подходит) --- */

export function Th({ align, sortable, sortDir, className, children, ...rest }) {
  return (
    <th
      className={cx(
        "ui-table__th",
        align === "center" && "ui-table__th--center",
        align === "right" && "ui-table__th--right",
        sortable && "ui-table__th--sortable",
        className,
      )}
      {...rest}
    >
      {children}
      {sortable && (
        <span className="ui-table__sort-icon">
          {sortDir === "asc" ? "▲" : sortDir === "desc" ? "▼" : "↕"}
        </span>
      )}
    </th>
  );
}

export function Td({ align, className, children, ...rest }) {
  return (
    <td
      className={cx(
        "ui-table__td",
        align === "center" && "ui-table__td--center",
        align === "right" && "ui-table__td--right",
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}

export function Tr({ clickable, className, children, ...rest }) {
  return (
    <tr
      className={cx(
        "ui-table__row",
        clickable && "ui-table__row--clickable",
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  );
}

/**
 * Table — data-driven таблица.
 *
 * @param {Array<{
 *   key: string,
 *   title: React.ReactNode,
 *   align?: "left"|"center"|"right",
 *   width?: string|number,
 *   render?: (row, rowIndex) => React.ReactNode   // кастомный рендер ячейки
 * }>} columns  Описание колонок.
 * @param {Array<object>} data          Строки.
 * @param {(row, index) => string|number} [rowKey]  Ключ строки (по умолчанию row.id ?? index).
 * @param {(row, index) => void} [onRowClick]       Клик по строке.
 * @param {boolean} [loading]           Показать skeleton.
 * @param {number}  [skeletonRows=5]    Кол-во строк-скелетонов.
 * @param {React.ReactNode} [empty="Нет данных"]  Что показать при пустом data.
 * @param {boolean} [zebra=true] @param {boolean} [hover=true] @param {boolean} [stickyHeader=true]
 * @param {(row, index) => string} [rowClassName]  Доп. класс на строку (напр. подсветка статуса).
 * @param {(row, index) => object} [rowStyle]       Доп. inline-стиль на строку.
 * @param {string}  [className] @param {object} [style]  На контейнер .ui-table-wrap.
 */
export default function Table({
  columns = [],
  data = [],
  rowKey,
  onRowClick,
  loading = false,
  skeletonRows = 5,
  empty = "Нет данных",
  zebra = true,
  hover = true,
  rowClassName,
  rowStyle,
  className,
  style,
}) {
  const keyOf = (row, i) =>
    rowKey ? rowKey(row, i) : row?.id ?? i;

  return (
    <div className={cx("ui-table-wrap", className)} style={style}>
      <table className={cx("ui-table", zebra && "ui-table--zebra", hover && "ui-table--hover")}>
        <thead>
          <tr>
            {columns.map((col) => (
              <Th
                key={col.key}
                align={col.align}
                sortable={col.sortable}
                sortDir={col.sortDir}
                onClick={col.onSort}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.title}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: skeletonRows }).map((_, r) => (
              <tr className="ui-table__row" key={`sk-${r}`}>
                {columns.map((col) => (
                  <td className="ui-table__td" key={col.key}>
                    <div className="ui-table__skeleton" />
                  </td>
                ))}
              </tr>
            ))
          ) : data.length === 0 ? (
            <tr>
              <td className="ui-table__state" colSpan={columns.length || 1}>
                {empty}
              </td>
            </tr>
          ) : (
            data.map((row, i) => (
              <Tr
                key={keyOf(row, i)}
                clickable={!!onRowClick}
                onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                className={rowClassName ? rowClassName(row, i) : undefined}
                style={rowStyle ? rowStyle(row, i) : undefined}
              >
                {columns.map((col) => (
                  <Td key={col.key} align={col.align}>
                    {col.render ? col.render(row, i) : row[col.key]}
                  </Td>
                ))}
              </Tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
